<script setup>
import { computed, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';
import { REPLY_EDITOR_MODES } from './constants';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggleMode', 'setMode']);

const wootEditorReplyMode = useTemplateRef('wootEditorReplyMode');
const wootEditorPrivateMode = useTemplateRef('wootEditorPrivateMode');
const wootEditorPixMode = useTemplateRef('wootEditorPixMode');

const replyModeSize = useElementSize(wootEditorReplyMode);
const privateModeSize = useElementSize(wootEditorPrivateMode);
const pixModeSize = useElementSize(wootEditorPixMode);

/**
 * Computed to get the current active mode index
 * 0 = REPLY, 1 = NOTE, 2 = PIX
 * When disabled, show NOTE mode
 * @type {ComputedRef<number>}
 */
const activeModeIndex = computed(() => {
  if (props.disabled) return 1;
  switch (props.mode) {
    case REPLY_EDITOR_MODES.NOTE:
      return 1;
    case REPLY_EDITOR_MODES.PIX:
      return 2;
    default:
      return 0;
  }
});

/**
 * Computes the width of the sliding background chip in pixels
 * Includes 16px of padding in the calculation
 * @type {ComputedRef<string>}
 */
const width = computed(() => {
  let widthToUse;
  switch (activeModeIndex.value) {
    case 1:
      widthToUse = privateModeSize.width.value;
      break;
    case 2:
      widthToUse = pixModeSize.width.value;
      break;
    default:
      widthToUse = replyModeSize.width.value;
  }

  const widthWithPadding = widthToUse + 16;
  return `${widthWithPadding}px`;
});

/**
 * Computes the X translation value for the sliding background chip
 * Translates by the cumulative width of previous modes
 * @type {ComputedRef<string>}
 */
const translateValue = computed(() => {
  let xTranslate = 0;
  if (activeModeIndex.value >= 1) {
    xTranslate += replyModeSize.width.value + 16;
  }
  if (activeModeIndex.value >= 2) {
    xTranslate += privateModeSize.width.value + 16;
  }
  return `${xTranslate}px`;
});

const handleModeClick = mode => {
  if (props.disabled && mode === REPLY_EDITOR_MODES.REPLY) return;
  emit('setMode', mode);
};
</script>

<template>
  <div
    class="flex items-center w-auto h-8 p-1 transition-all border rounded-full bg-n-alpha-2 group relative duration-300 ease-in-out z-0"
    :class="{
      'cursor-not-allowed': disabled,
    }"
  >
    <button
      ref="wootEditorReplyMode"
      class="flex items-center gap-1 px-2 z-20 cursor-pointer"
      :class="{ 'opacity-50': disabled }"
      :disabled="disabled"
      @click="handleModeClick(REPLY_EDITOR_MODES.REPLY)"
    >
      {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
    </button>
    <button
      ref="wootEditorPrivateMode"
      class="flex items-center gap-1 px-2 z-20 cursor-pointer"
      @click="handleModeClick(REPLY_EDITOR_MODES.NOTE)"
    >
      {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
    </button>
    <button
      ref="wootEditorPixMode"
      class="flex items-center gap-1 px-2 z-20 cursor-pointer"
      @click="handleModeClick(REPLY_EDITOR_MODES.PIX)"
    >
      {{ $t('CONVERSATION.REPLYBOX.PIX') }}
    </button>
    <div
      class="absolute shadow-sm rounded-full h-6 w-[var(--chip-width)] ease-in-out translate-x-[var(--translate-x)] rtl:translate-x-[var(--rtl-translate-x)] bg-n-solid-1"
      :class="{
        'transition-all duration-300': !disabled,
      }"
      :style="{
        '--chip-width': width,
        '--translate-x': translateValue,
        '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
      }"
    />
  </div>
</template>
